import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import type { FileRouteTypes } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { FormInput } from '../../../../components/forms/FormInput';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart, PatientDoc, AlternateAgentInfo } from '@usacsmideo/types';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import type { AgentRequest } from '@usacsmideo/types';
import { Button } from '../../../../components/buttons/Button';
import { useMyAgents } from '../../../../hooks/useAgentRequests';
import { emailsMatch, isChartRecordComplete } from '../../../../data/agents/agentSlotConvergence';
import { isValidEmail } from '../../../../utils/isValidEmail';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import { FieldInfo } from '../../../../components/FieldInfo';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import * as Sentry from '@sentry/browser';

/**
 * ECN Subtask 9 (CU-86ahjxrgg) — careTeam Alternate Agent CHART form.
 *
 * Pure chart-data records (ALTERNATE_AGENT_INFO_1 / _2 — up to TWO alternates,
 * mirroring PATIENT_INFO_1/_2), parallel to hcaInfo. SEPARATE from the ECN
 * agentRequest invite, which lives on the My MIDEO Family hub (redesign §3.2,
 * "Version X"). NO power-of-attorney (HCA-only). Declinable: reached opt-in via
 * the "Add alternate agents" button on hcaInfo; a patient can Next past it, and
 * the second alternate is itself optional (revealed on demand).
 */
export const Route = createFileRoute('/_authenticated/$patientId/careTeam/alternateAgents')({
  component: AlternateAgentInfoPage,
});

type AltSlot = 'ALTERNATE_AGENT_INFO_1' | 'ALTERNATE_AGENT_INFO_2';

/**
 * One alternate-agent sub-form, bound to a specific chart slot. Owns its own
 * form state + Save (writes that slot only). Reused for alternate 1 and 2.
 */
function AlternateAgentFields({
  patientId,
  slot,
  label,
  existing,
  linkedRequest,
}: {
  patientId: string;
  slot: AltSlot;
  label: string;
  existing: AlternateAgentInfo | undefined;
  /**
   * The live (waiting/accepted) agentRequest this slot's stamped `agentRequestId`
   * points at, if any. Used to warn BEFORE the patient commits an email change
   * that would orphan an active invite (CU-86aj2q4fc).
   */
  linkedRequest: AgentRequest | undefined;
}) {
  const { mutate } = useUpdateChartSection();
  const navigate = useNavigate();
  const [saved, setSaved] = React.useState(false);
  // Live email value drives the before-commit orphan warning.
  const [emailValue, setEmailValue] = React.useState(existing?.email ?? '');

  const form = useForm({
    defaultValues: {
      agent: existing?.agent ?? '',
      street: existing?.street ?? '',
      city: existing?.city ?? '',
      state: existing?.state ?? '',
      ZIP: existing?.ZIP ?? '',
      country: existing?.country ?? '',
      email: existing?.email ?? '',
      primaryPhone: existing?.primaryPhone ?? '',
      agentToPatient: existing?.agentToPatient ?? '',
      patientToAgent: existing?.patientToAgent ?? '',
    },
    onSubmit: () => {
      const v = form.state.values;
      const info: AlternateAgentInfo = {
        agent: v.agent,
        street: v.street,
        city: v.city,
        state: v.state,
        ZIP: v.ZIP,
        country: v.country,
        email: v.email,
        primaryPhone: v.primaryPhone,
        agentToPatient: v.agentToPatient,
        patientToAgent: v.patientToAgent,
      };
      // docData is a single-key ChartDocData member; build the slot-specific wrapper.
      const docData =
        slot === 'ALTERNATE_AGENT_INFO_1'
          ? { ALTERNATE_AGENT_INFO_1: info }
          : { ALTERNATE_AGENT_INFO_2: info };
      mutate(
        { patientId, docData },
        {
          onSuccess: () => setSaved(true),
          onError: (error) => {
            Sentry.captureException(error);
            alert(`Error updating document: ${error.message}`);
          },
        },
      );
    },
  });

  const FIELDS: { name: keyof typeof form.state.values; label: string }[] = [
    { name: 'agent', label: "Alternate Agent's Full Name" },
    { name: 'email', label: 'Email' },
    { name: 'primaryPhone', label: 'Primary Phone' },
    { name: 'street', label: 'Street Address' },
    { name: 'city', label: 'City' },
    { name: 'state', label: 'State' },
    { name: 'ZIP', label: 'ZIP Code' },
    { name: 'country', label: 'Country' },
    { name: 'agentToPatient', label: "This agent's relationship to you (e.g. son, friend)" },
    { name: 'patientToAgent', label: 'Your relationship to this agent (e.g. mother, friend)' },
  ];

  // Orphan warning: the slot has a LIVE invite, but the email now in the field no
  // longer matches the invite's recipientEmail — committing this Save will
  // disconnect the active invite. Surfaced before commit (and persists after).
  const willOrphanInvite =
    !!linkedRequest &&
    (linkedRequest.status === 'waiting' || linkedRequest.status === 'accepted') &&
    !emailsMatch(emailValue, linkedRequest.recipientEmail);

  return (
    <section className="flex flex-col gap-2 rounded-md border border-gray-200 bg-white p-4">
      <h2 className="text-lg font-semibold text-sky-800">{label}</h2>

      {willOrphanInvite && (
        <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" role="alert">
          Changing this email will disconnect {existing?.agent || 'this agent'}&rsquo;s active
          invitation — they will stop receiving alerts. After saving, return to My MIDEO Family to
          re-invite them.
        </p>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="flex flex-col">
          {FIELDS.map((f) => (
            <form.Field
              key={f.name}
              name={f.name}
              // Belt-and-suspenders email-format validation on the email field
              // only — catch an obviously-invalid address before submit so the
              // patient fixes it without a round-trip. The cloud function is the
              // hard gate; this is the exact failure that used to silently
              // orphan the invite (MIDEO-UNIFIED-18). Empty stays allowed here:
              // completeness is enforced by isChartRecordComplete before invite.
              validators={
                f.name === 'email'
                  ? {
                      onChange({ value }: { value: string }) {
                        if (value && value.length > 0 && !isValidEmail(value)) {
                          return 'Please enter a valid email address.';
                        }
                        return undefined;
                      },
                    }
                  : undefined
              }
              children={(field) => (
                <>
                  <FormInput
                    label={f.label}
                    id={`${slot}-${f.name}`}
                    name={field.name}
                    value={field.state.value}
                    handleBlur={field.handleBlur}
                    handleChange={(e) => {
                      field.handleChange(e.target.value);
                      setSaved(false);
                      // Track the email field live to drive the orphan warning.
                      if (f.name === 'email') setEmailValue(e.target.value);
                    }}
                  />
                  <FieldInfo fieldMeta={field.state.meta} />
                </>
              )}
            />
          ))}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <form.Subscribe
            selector={(state) => [state.canSubmit]}
            children={([canSubmit]) => (
              <Button
                id={`save-${slot}`}
                type="submit"
                disabled={!canSubmit}
                onClick={form.handleSubmit}
                textContent="Save"
                submitted={saved}
              />
            )}
          />
          {saved && <span className="text-sm text-green-700">Saved.</span>}
        </div>
      </form>

      {/* Cross-link back to the hub once this record is complete — the invite fires
          there (page-concern separation). Dynamic per-agent text; "manage" when a
          live invite already exists, else "invite". */}
      {isChartRecordComplete(existing) && (
        <Button
          id={`go-to-hub-${slot}`}
          type="button"
          textContent={
            linkedRequest &&
            (linkedRequest.status === 'waiting' || linkedRequest.status === 'accepted')
              ? `Manage ${existing?.agent || 'this agent'}'s alerts in My MIDEO Family`
              : `Invite ${existing?.agent || 'this agent'} to receive alerts`
          }
          onClick={() => navigate({ to: '/myMideoFamily' })}
        />
      )}
    </section>
  );
}

function AlternateAgentInfoPage() {
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;

  // Alternate invites, indexed by doc id, so each slot can find the request its
  // stamped agentRequestId points at (for the before-commit orphan warning).
  const { partition } = useMyAgents();
  const altRequestsById = React.useMemo(() => {
    const map = new Map<string, AgentRequest>();
    partition.all.filter((r) => r.agentType === 'alternateAgent').forEach((r) => map.set(r.id, r));
    return map;
  }, [partition.all]);
  const linkedFor = (rec: AlternateAgentInfo | undefined): AgentRequest | undefined =>
    rec?.agentRequestId ? altRequestsById.get(rec.agentRequestId) : undefined;

  // Show the second alternate slot if it already has data, else on demand.
  const [showSecond, setShowSecond] = React.useState(false);

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo,
      chart,
    });

    if ('incompleteMessage' in res) {
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => navigate({ to: '/' }),
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(`Error updating navObj for patient ${patientId}: ${error.message}`),
            );
            alert('There was an error updating your information. Please try again later.');
          },
        },
      );
      return;
    }

    mutatePatientDoc(
      { patientId, navObj: res.updatedNavObj },
      {
        onSuccess: () => {
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(`Error updating navObj for patient ${patientId}: ${error.message}`),
          );
          alert('There was an error updating your information. Please try again later.');
        },
      },
    );
  };

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser.uid !== patientId) {
    return <Unauthorized />;
  }

  const alt1 = chart.ALTERNATE_AGENT_INFO_1;
  const alt2 = chart.ALTERNATE_AGENT_INFO_2;
  const secondVisible = showSecond || !!alt2?.agent;

  return (
    <>
      <div className="flex w-full max-w-3xl flex-col gap-4">
        <h1 className="place-self-center text-3xl font-bold">ALTERNATE AGENT INFORMATION</h1>
        <p className="place-self-center text-center text-gray-600">
          An alternate agent can act on your behalf if your Healthcare Agent is unavailable. This
          is optional — you can designate up to two, or continue without adding any.
        </p>

        <AlternateAgentFields
          patientId={patientId}
          slot="ALTERNATE_AGENT_INFO_1"
          label="Alternate Agent 1"
          existing={alt1}
          linkedRequest={linkedFor(alt1)}
        />

        {secondVisible ? (
          <AlternateAgentFields
            patientId={patientId}
            slot="ALTERNATE_AGENT_INFO_2"
            label="Alternate Agent 2"
            existing={alt2}
            linkedRequest={linkedFor(alt2)}
          />
        ) : (
          <Button
            id="add-second-alternate"
            type="button"
            textContent="Add a second alternate agent"
            onClick={() => setShowSecond(true)}
          />
        )}

        <div className="my-4 flex flex-col items-center">
          <button
            id="alternate-next"
            title="Continue"
            onClick={handleNextButtonClick}
            className={classNames.nextButton}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}

export default AlternateAgentInfoPage;
